import { useState } from "react";
import { GitCompare, History, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Empty, ErrorState, Loading } from "@/components/States";

const label = (e) => e.data?.name || e.data?.text?.slice(0, 40) || e.data?.caption || e.type;

function diff(snap, cur) {
  const a = Object.fromEntries(snap.elements.map((e) => [e.id, e]));
  const b = Object.fromEntries(cur.elements.map((e) => [e.id, e]));
  return {
    added: cur.elements.filter((e) => !a[e.id]),
    removed: snap.elements.filter((e) => !b[e.id]),
    changed: cur.elements.filter((e) => a[e.id] && JSON.stringify(a[e.id]) !== JSON.stringify(e)),
    rooms: cur.rooms.length - snap.rooms.length,
  };
}

const List = ({ title, items, tone }) => items.length ? (
  <div><p className={`mb-1 text-xs font-semibold ${tone}`}>{title} ({items.length})</p>
    <ul className="space-y-0.5 text-sm text-muted-foreground">{items.slice(0, 8).map((e) => <li key={e.id} className="truncate">· {label(e)}</li>)}</ul></div>
) : null;

export default function VersionsDialog({ open, onClose, board, onRestored, canEdit }) {
  const { data, error, loading, reload } = useFetch(open ? `/boards/${board.id}/versions` : null);
  const [name, setName] = useState("");
  const [cmp, setCmp] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post(`/boards/${board.id}/versions`, { name }); setName(""); toast.success("Version saved"); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const restore = async (v) => {
    try { const { data: b } = await api.post(`/boards/${board.id}/versions/${v.id}/restore`); onRestored(b); toast.success(`Restored "${v.name}"`); onClose(); } catch (err) { toast.error(errMsg(err)); }
  };
  const d = cmp && diff(cmp.snapshot, board);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-3xl font-normal">Board versions</DialogTitle></DialogHeader>
        {canEdit && (
          <form onSubmit={save} className="flex gap-2">
            <Input required placeholder="Version name, e.g. Client review 1" value={name} onChange={(e) => setName(e.target.value)} data-testid="version-name-input" />
            <Button type="submit" data-testid="version-save-button">Save current</Button>
          </form>
        )}
        {loading ? <Loading rows={2} /> : error ? <ErrorState message={error} onRetry={reload} /> : !data?.length ? (
          <Empty icon={History} title="No versions yet" text="Save a version before big changes so you can compare or roll back." />
        ) : (
          <div className="space-y-2">
            {data.map((v) => (
              <div key={v.id} className="flex flex-wrap items-center gap-2 rounded-lg border p-3" data-testid={`version-${v.id}`}>
                <div className="min-w-0 flex-1"><p className="text-sm font-medium">{v.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">{v.created_at?.slice(0, 16).replace("T", " ")} · {v.snapshot.elements.length} items · {v.created_by}</p></div>
                <Button size="sm" variant="outline" onClick={() => setCmp(cmp?.id === v.id ? null : v)} data-testid={`version-compare-${v.id}`}><GitCompare className="mr-1 h-4 w-4" />Compare</Button>
                {canEdit && <Button size="sm" variant="ghost" onClick={() => restore(v)} data-testid={`version-restore-${v.id}`}><RotateCcw className="mr-1 h-4 w-4" />Restore</Button>}
              </div>
            ))}
          </div>
        )}
        {d && (
          <div className="space-y-3 rounded-lg bg-secondary/60 p-4" data-testid="version-diff">
            <p className="text-sm font-semibold">"{cmp.name}" → current board</p>
            {!d.added.length && !d.removed.length && !d.changed.length && !d.rooms && <p className="text-sm text-muted-foreground">No differences.</p>}
            {d.rooms !== 0 && <p className="text-sm">Rooms {d.rooms > 0 ? `+${d.rooms}` : d.rooms}</p>}
            <List title="Added" items={d.added} tone="text-emerald-700" />
            <List title="Removed" items={d.removed} tone="text-destructive" />
            <List title="Moved or edited" items={d.changed} tone="text-accent" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
